import { Component, inject, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { ToastrService } from 'ngx-toastr';
import { Subject, Subscription } from 'rxjs';
import { ConfirmationModalComponent } from '../../../../../_shared/components/modals/confirmation-modal/confirmation-modal.component';
import { ErrorHandlerService } from '../../../../../_shared/services/error-handler.service';
import { ComplianceTasksTableComponent } from '../../../_shared/components/compliance-tasks/compliance-tasks-table.component';
import { ComplianceStateService } from '../../../_shared/services/compliance-state.service';
import { ComplianceOverviewService } from '../../services/compliance-overview.service';
import {
  RescheduleComplianceTaskModalComponent,
  RescheduleComplianceTaskModalRequest
} from '../../../_shared/components/modals/reschedule-compliance-task-modal/reschedule-compliance-task-modal.component';
import { AssignRoutineComplianceTaskWorkshopModalComponent } from '../modals/assign-routine-compliance-task-workshop-modal/assign-routine-compliance-task-workshop-modal.component';
import {
  ComplianceStatus,
  ComplianceStatusFilter,
  CompliancePhase,
  ComplianceTasksTableRowAction,
  RoutineComplianceTask,
  RoutineComplianceTaskLeg,
  RoutineComplianceTaskRow,
  RoutineComplianceTasksRequest,
  TableColumn
} from '../../models/compliance-item.model';
import {
  routineChecklistEntityKindFromLabel,
  routineChecklistLegLabelKeys,
  RoutineChecklistEntityKind,
  routineComplianceTasksPhaseHeaderKeys,
} from '../../../_shared/models/routine-checklist-display.model';
import { resolveInspectionTypeId } from '../../models/routine-video.model';
import { ComplianceTasksTableAction } from '../../../_shared/enums/compliance-tasks-table-action.enum';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { translateRoutineTypeName } from '../../../_shared/helpers/compliance-lookup-label.helper';

@Component({
  selector: 'app-routine-compliance-tasks',
  standalone: true,
  imports: [
    ComplianceTasksTableComponent,
    TranslocoPipe
  ],
  templateUrl: './routine-compliance-tasks.component.html',
  styleUrl: './routine-compliance-tasks.component.scss'
})
export class RoutineComplianceTasksComponent implements OnInit, OnDestroy {
  private router: Router = inject(Router);
  private complianceOverviewService: ComplianceOverviewService = inject(ComplianceOverviewService);
  private complianceOverviewStateService: ComplianceStateService = inject(ComplianceStateService);
  private errorService: ErrorHandlerService = inject(ErrorHandlerService);
  private modalService: NgbModal = inject(NgbModal);
  private toastr: ToastrService = inject(ToastrService);
  private transloco: TranslocoService = inject(TranslocoService);

  @ViewChild(ComplianceTasksTableComponent) private tasksTable?: ComplianceTasksTableComponent;

  private requestSubscription?: Subscription;
  private readonly cancelSubject = new Subject<void>();

  private accountIds: number[] = [];
  private searchTerm: string | null = null;
  private selectedStatus: ComplianceStatusFilter = ComplianceStatusFilter.All;

  routineComplianceTasks: RoutineComplianceTaskRow[] = [];
  isLoading = true;
  isLoadingMore = false;
  isRefreshing = false;
  hasError = false;
  currentPage = 1;
  pageSize = 20;
  totalPages = 0;
  totalCount = 0;

  get columns(): TableColumn[] {
    const phaseHeaderKeys = routineComplianceTasksPhaseHeaderKeys(this.resolveRoutineTasksColumnKind());
    return [
      { header: this.transloco.translate('compliance.overview.routineTasks.columns.asset'), value: 'assetName', width: 'col', type: 'text', truncate: true, title: true, bold: true },
      { header: this.transloco.translate('compliance.overview.routineTasks.columns.routineType'), value: 'checklistTypeLabel', width: 'col', type: 'text' },
      { header: this.transloco.translate('compliance.overview.routineTasks.columns.complianceStandard'), value: 'complianceTask', width: 'col', type: 'text', truncate: true, title: true },
      { header: this.transloco.translate(phaseHeaderKeys.preStatus), value: 'preComplianceStatus', width: 'col', type: 'status' },
      { header: this.transloco.translate(phaseHeaderKeys.postStatus), value: 'postComplianceStatus', width: 'col', type: 'status' },
      { header: this.transloco.translate('compliance.overview.routineTasks.columns.action'), value: 'action', width: 'action-col', type: 'expand' },
    ];
  }

  readonly subColumns: TableColumn[] = [
    { header: this.transloco.translate('compliance.overview.routineTasks.subColumns.routineType'), value: 'routineTypeLabel', width: 'col', type: 'text', bold: true },
    { header: this.transloco.translate('compliance.overview.routineTasks.subColumns.dueDate'), value: 'dueDateTime', width: 'col', type: 'date' },
    { header: this.transloco.translate('compliance.overview.routineTasks.subColumns.workshop'), value: 'workshop', width: 'col', type: 'nullable-text' },
    { header: this.transloco.translate('compliance.overview.routineTasks.subColumns.inspectedBy'), value: 'inspectedBy', width: 'col', type: 'nullable-text' },
    { header: this.transloco.translate('compliance.overview.routineTasks.subColumns.completedOn'), value: 'completedOn', width: 'col', type: 'date' },
    { header: this.transloco.translate('compliance.overview.routineTasks.subColumns.status'), value: 'complianceStatus', width: 'col', type: 'status' },
    { header: this.transloco.translate('compliance.overview.routineTasks.subColumns.action'), value: 'action', width: 'action-col', type: 'sub-action' },
  ];

  private toRows(tasks: RoutineComplianceTask[]): RoutineComplianceTaskRow[] {
    return tasks.map(task => {
      const legLabelKeys = routineChecklistLegLabelKeys(routineChecklistEntityKindFromLabel(task.checklistType));
      const legs: RoutineComplianceTaskLeg[] = [];

      if (resolveInspectionTypeId(task.routineTypeId, 'pre') !== null) {
        legs.push({
          phase: 'pre',
          routineTypeLabel: this.transloco.translate(legLabelKeys.pre),
          dueDateTime: task.preDateTime,
          workshop: task.workshop,
          inspectedBy: task.preInspector,
          inspectedByRole: task.preInspectorRole,
          completedOn: task.preCompletedAt,
          complianceStatus: task.preComplianceStatus,
          actions: this.legActions('pre', task.preComplianceStatus)
        });
      }

      if (resolveInspectionTypeId(task.routineTypeId, 'post') !== null) {
        legs.push({
          phase: 'post',
          routineTypeLabel: this.transloco.translate(legLabelKeys.post),
          dueDateTime: task.postDateTime,
          workshop: task.workshop,
          inspectedBy: task.postInspector,
          inspectedByRole: task.postInspectorRole,
          completedOn: task.postCompletedAt,
          complianceStatus: task.postComplianceStatus,
          actions: this.legActions('post', task.postComplianceStatus)
        });
      }

      return { ...task, legs, checklistTypeLabel: translateRoutineTypeName(this.transloco, task.checklistType) };
    });
  }

  private legActions(phase: CompliancePhase, status: ComplianceStatus | null): ComplianceTasksTableRowAction[] {
    const actions: ComplianceTasksTableRowAction[] = [];

    if (status !== ComplianceStatus.Completed) {
      actions.push({ label: this.transloco.translate('compliance.overview.routineTasks.actions.assignWorkshop'), action: ComplianceTasksTableAction.AssignWorkshop });
    }

    if (status === ComplianceStatus.Pending || status === ComplianceStatus.Overdue) {
      actions.push({
        label: this.transloco.translate('compliance.overview.routineTasks.actions.reschedule'),
        action: phase === 'pre'
          ? ComplianceTasksTableAction.ReschedulePre
          : ComplianceTasksTableAction.ReschedulePost
      });
    }

    // Deletes the whole compliance schedule, not just this phase — hence the wording.
    actions.push({ label: this.transloco.translate('compliance.overview.routineTasks.actions.deleteSchedule'), action: ComplianceTasksTableAction.Delete, danger: true });

    return actions;
  }

  private resolveRoutineTasksColumnKind(): RoutineChecklistEntityKind {
    const tasks = this.routineComplianceTasks;
    if (!tasks.length) {
      return 'trip';
    }
    const kinds = [...new Set(tasks.map(t => routineChecklistEntityKindFromLabel(t.checklistType)))];
    return kinds.length === 1 ? kinds[0] : 'generic';
  }

  ngOnInit(): void {
    this.requestSubscription = this.complianceOverviewStateService.request$.subscribe(request => {
      this.accountIds = request.accountIds ?? [];

      this.resetAndLoad();
    });
  }

  ngOnDestroy(): void {
    this.requestSubscription?.unsubscribe();
    this.cancelSubject.next();
    this.cancelSubject.complete();
  }

  async getRoutineComplianceTasksAsync(resetResults: boolean = false): Promise<void> {
    this.hasError = false;
    const requestedPage = this.currentPage;
    const request = this.buildRequest(requestedPage);

    try {
      if (requestedPage === 1 || resetResults) {
        this.isLoading = true;
        this.isLoadingMore = false;
        this.cancelSubject.next();
      } else {
        this.isLoadingMore = true;
      }

      const results = await this.complianceOverviewService.getRoutineComplianceTasksAsync(request, this.cancelSubject);
      const pagination = results.data;
      const tasks = pagination?.results ?? [];

      const rows = this.toRows(tasks);
      if (requestedPage === 1) {
        this.routineComplianceTasks = rows;
      } else {
        this.routineComplianceTasks = [...this.routineComplianceTasks, ...rows];
      }

      this.totalCount = pagination?.totalCount ?? 0;
      this.totalPages = pagination?.totalPages ?? 0;

      this.isLoading = false;
      this.isLoadingMore = false;
    } catch (error: any) {
      this.errorService.handleError(error, {
        callback: () => {
          this.hasError = true;
          this.isLoading = false;
          this.isLoadingMore = false;
        }
      });
    }
  }

  onRowClick(item: RoutineComplianceTask): void {
    this.router.navigate(['/asset-management/compliance-overview/asset-routine-compliance-details'], {
      queryParams: {
        assetName: item.assetName,
        complianceId: item.complianceId,
        assetId: item.assetId,
        routineTypeId: item.routineTypeId,
        routineComplianceTaskId: item.id,
        checklistType: item.checklistType
      }
    });
  }

  onAssignWorkshop(item: RoutineComplianceTask): void {
    const scrollOffset = this.tasksTable?.measureScrollOffset() ?? 0;
    const previousIndex = this.routineComplianceTasks.findIndex(t => t.id === item.id);

    const modalRef = this.modalService.open(AssignRoutineComplianceTaskWorkshopModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.accountId = item.accountId;
    modalRef.componentInstance.task = item;
    modalRef.closed.subscribe((workshopName?: string) => {
      if (!workshopName) {
        return;
      }

      void this.refreshLoadedTasksAsync(item.id, previousIndex, scrollOffset);
    });
  }

  private async refreshLoadedTasksAsync(taskId: number, previousIndex: number, scrollOffset: number): Promise<void> {
    const pagesLoaded = Math.max(this.currentPage, 1);
    const request: RoutineComplianceTasksRequest = {
      ...this.buildRequest(1),
      pageSize: this.pageSize * pagesLoaded
    };

    this.isRefreshing = true;

    try {
      const results = await this.complianceOverviewService.getRoutineComplianceTasksAsync(request, this.cancelSubject);
      const pagination = results.data;

      this.routineComplianceTasks = this.toRows(pagination?.results ?? []);
      this.totalCount = pagination?.totalCount ?? 0;
      this.totalPages = Math.ceil(this.totalCount / this.pageSize);

      this.restoreScrollPosition(taskId, previousIndex, scrollOffset);
    } catch (error: any) {
      this.errorService.handleError(error, { showToast: true });
    } finally {
      this.isRefreshing = false;
    }
  }

  private restoreScrollPosition(taskId: number, previousIndex: number, scrollOffset: number): void {
    const index = this.routineComplianceTasks.findIndex(t => t.id === taskId);
    if (index === -1) {
      return;
    }

    setTimeout(() => {
      if (index === previousIndex) {
        this.tasksTable?.scrollToOffset(scrollOffset);
      } else {
        this.tasksTable?.scrollToIndex(index);
      }
    });
  }

  onReschedule(item: RoutineComplianceTask, phase: CompliancePhase): void {
    const scrollOffset = this.tasksTable?.measureScrollOffset() ?? 0;
    const previousIndex = this.routineComplianceTasks.findIndex(t => t.id === item.id);

    const modalRef = this.modalService.open(RescheduleComplianceTaskModalComponent, {
      centered: true,
      backdrop: 'static'
    });
    const initialDate = phase === 'pre' ? item.preDateTime : item.postDateTime;
    modalRef.componentInstance.initialDate = initialDate ?? new Date();
    modalRef.componentInstance.showNotifyPersonResponsible = false;
    modalRef.componentInstance.enableTime = true;
    modalRef.componentInstance.successMessage = this.transloco.translate(
      phase === 'pre'
        ? 'compliance.overview.routineTasks.toasts.rescheduledPre'
        : 'compliance.overview.routineTasks.toasts.rescheduledPost'
    );
    modalRef.componentInstance.submitHandler = async (
      request: RescheduleComplianceTaskModalRequest,
      cancellation: Subject<void>
    ) => {
      await this.complianceOverviewService.rescheduleRoutineComplianceTaskAsync({
        id: item.id,
        assetId: item.assetId,
        dueDate: request.dueDate,
        phase
      }, cancellation);
    };
    modalRef.closed.subscribe((shouldReload: boolean) => {
      if (!shouldReload) {
        return;
      }

      void this.refreshLoadedTasksAsync(item.id, previousIndex, scrollOffset);
    });
  }

  onDelete(item: RoutineComplianceTask): void {
    const modalRef = this.modalService.open(ConfirmationModalComponent, {
      centered: true,
      keyboard: false,
      backdrop: 'static',
      windowClass: 'confirmation-modal__content'
    });

    modalRef.componentInstance.data = {
      title: this.transloco.translate('compliance.overview.routineTasks.confirmations.delete.title'),
      message: this.transloco.translate('compliance.overview.routineTasks.confirmations.delete.message'),
      warningMessage: this.transloco.translate('compliance.overview.routineTasks.confirmations.delete.warning'),
      confirmationPrompt: 'delete',
      confirmPlaceholder: this.transloco.translate('compliance.overview.routineTasks.confirmations.delete.placeholder'),
      confirmText: this.transloco.translate('compliance.overview.routineTasks.confirmations.delete.confirm'),
      declineText: this.transloco.translate('compliance.overview.routineTasks.confirmations.delete.decline'),
      closeOnSuccess: false,
      onConfirm: async () => {
        try {
          await this.complianceOverviewService.deleteRoutineComplianceTaskAsync({
            id: item.id,
            assetId: item.assetId
          });
          this.toastr.success(this.transloco.translate('compliance.overview.routineTasks.toasts.deleted'));
          modalRef.close(true);
        } catch (error: any) {
          this.errorService.handleError(error, {
            showToast: true
          });
        }
      }
    };

    modalRef.closed.subscribe((shouldReload: boolean) => {
      if (shouldReload) {
        this.resetAndLoad();
      }
    });
  }

  onSearch(term: string): void {
    this.searchTerm = term?.trim() || null;
    this.resetAndLoad();
  }

  onStatusFilter(status: ComplianceStatusFilter): void {
    this.selectedStatus = status;
    this.resetAndLoad();
  }

  onScrollDown(): void {
    if (this.isLoading || this.isLoadingMore || this.isRefreshing || this.currentPage >= this.totalPages) {
      return;
    }

    this.currentPage++;
    void this.getRoutineComplianceTasksAsync();
  }

  private resetAndLoad(): void {
    this.currentPage = 1;
    this.totalPages = 0;
    this.totalCount = 0;
    this.routineComplianceTasks = [];
    void this.getRoutineComplianceTasksAsync(true);
  }

  private buildRequest(page: number): RoutineComplianceTasksRequest {
    const filters = this.complianceOverviewStateService.getCurrentRequest();
    const region = filters?.complianceRegion;

    return {
      accountIds: filters?.accountIds ?? [],
      page,
      pageSize: this.pageSize,
      regionId: region?.type === 'region' ? region.id : null,
      countryId: region?.type === 'country' ? region.id : null,
      complianceStatus: this.selectedStatus === ComplianceStatusFilter.All
        ? null
        : this.selectedStatus as unknown as ComplianceStatus,
      complianceId: filters?.complianceId ?? null,
      assetTypeId: filters?.assetTypeId ?? null,
      searchTerm: this.searchTerm
    };
  }
}